import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Grid, Alert, Typography, Divider, Checkbox, FormControlLabel, Box, Chip
} from '@mui/material';
import { contractsApi } from '../../services/api';

interface Props {
  open: boolean;
  contract: any;
  amendmentNumber: number;
  onClose: () => void;
  onCreated: () => void;
}

const AMENDABLE_FIELDS: { field: string; label: string; type: 'text' | 'date' | 'number' | 'select' }[] = [
  { field: 'contract_value',          label: 'Contract Value',            type: 'number' },
  { field: 'expiration_date',         label: 'Expiration Date',           type: 'date' },
  { field: 'end_date',                label: 'End Date',                  type: 'date' },
  { field: 'start_date',              label: 'Start Date',                type: 'date' },
  { field: 'payment_terms',           label: 'Payment Terms',             type: 'text' },
  { field: 'payment_terms_days',      label: 'Payment Terms (days)',      type: 'number' },
  { field: 'notice_period_days',      label: 'Notice Period (days)',      type: 'number' },
  { field: 'renewal_term_months',     label: 'Renewal Term (months)',     type: 'number' },
  { field: 'renewal_terms',           label: 'Renewal Terms',             type: 'text' },
  { field: 'billing_frequency',       label: 'Billing Frequency',         type: 'text' },
  { field: 'billing_milestones',      label: 'Billing Milestones',        type: 'text' },
  { field: 'late_fees_pct',           label: 'Late Fees %',               type: 'number' },
  { field: 'taxes_pass_through',      label: 'Taxes / Pass-Through',      type: 'text' },
  { field: 'credits_penalties_refunds', label: 'Credits, Penalties & Refunds', type: 'text' },
  { field: 'governing_law',           label: 'Governing Law',             type: 'text' },
  { field: 'jurisdiction',            label: 'Jurisdiction',              type: 'text' },
  { field: 'venue_jurisdiction',      label: 'Venue / Jurisdiction',      type: 'text' },
  { field: 'assignment_rights',       label: 'Assignment Rights',         type: 'text' },
  { field: 'amendment_requirements',  label: 'Amendment Requirements',    type: 'text' },
  { field: 'description',             label: 'Description',               type: 'text' },
  { field: 'scope_summary',           label: 'Scope Summary',             type: 'text' },
  { field: 'legal_entity',            label: 'DBA / Trade Name',          type: 'text' },
  { field: 'department',              label: 'Department',                type: 'text' },
  { field: 'currency',                label: 'Currency',                  type: 'text' },
];

export default function AmendmentDialog({ open, contract, amendmentNumber, onClose, onCreated }: Props) {
  const [amendmentName, setAmendmentName] = useState(`Amendment #${amendmentNumber}`);
  const [effectiveDate, setEffectiveDate] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [newValues, setNewValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleToggle = (field: string) => {
    setSelected(prev => ({ ...prev, [field]: !prev[field] }));
    if (!newValues[field]) {
      const cur = contract[field];
      setNewValues(prev => ({ ...prev, [field]: cur != null ? String(cur) : '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const changes: Record<string, any> = {};
    for (const f of AMENDABLE_FIELDS) {
      if (selected[f.field]) {
        const raw = newValues[f.field];
        changes[f.field] = raw === '' ? null : (f.type === 'number' ? parseFloat(raw) || null : raw);
      }
    }
    if (Object.keys(changes).length === 0) {
      setError('Select at least one field to amend.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await contractsApi.createAmendment(contract.id, {
        amendment_name: amendmentName,
        effective_date: effectiveDate || null,
        description,
        changes
      });
      onCreated();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to apply amendment');
    } finally {
      setLoading(false);
    }
  };

  const formatOldValue = (field: string) => {
    const v = contract[field];
    if (v == null || v === '') return <em style={{ color: '#999' }}>not set</em>;
    return String(v);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Apply Amendment to Contract</DialogTitle>
      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Amendment Name" value={amendmentName} onChange={e => setAmendmentName(e.target.value)} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Effective Date" type="date" value={effectiveDate} onChange={e => setEffectiveDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth required multiline rows={2} label="Description of Amendment" value={description} onChange={e => setDescription(e.target.value)} placeholder="Summarize what this amendment changes and why" />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Select fields to amend — current values shown, enter new values below
          </Typography>

          {AMENDABLE_FIELDS.map(({ field, label, type }) => (
            <Box key={field} sx={{ mb: selected[field] ? 2 : 0.5 }}>
              <FormControlLabel
                control={<Checkbox checked={!!selected[field]} onChange={() => handleToggle(field)} size="small" />}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">{label}</Typography>
                    {!selected[field] && (
                      <Chip label={formatOldValue(field)} size="small" variant="outlined" sx={{ maxWidth: 220, fontSize: 11 }} />
                    )}
                  </Box>
                }
              />
              {selected[field] && (
                <Grid container spacing={1} sx={{ ml: 4, mt: 0 }}>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth size="small" label="Current value" value={contract[field] ?? ''} disabled
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <TextField
                      fullWidth size="small" label="New value" type={type === 'date' ? 'date' : type === 'number' ? 'number' : 'text'}
                      value={newValues[field] ?? ''}
                      onChange={e => setNewValues(prev => ({ ...prev, [field]: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      multiline={type === 'text' && ['description', 'scope_summary', 'renewal_terms', 'billing_milestones', 'assignment_rights', 'amendment_requirements', 'credits_penalties_refunds'].includes(field)}
                      rows={type === 'text' ? 2 : undefined}
                    />
                  </Grid>
                </Grid>
              )}
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading || !description}>
            {loading ? 'Applying...' : 'Apply Amendment'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
